import React from 'react';

export const PRIVACY_POLICY_EFFECTIVE_DATE = 'October 4, 2026';
const SUPPORT_EMAIL = 'support@paddiq.app';

export function PrivacyPolicyContent() {
  return (
    <article className="policy-content">
      <section id="email-choices">
        <h2>Email communications and your choices</h2>
        <p>
          Marketing emails, including product news, tips, and offers, are optional. We ask you to
          choose through Email preferences in your account settings. Accounts with no recorded email
          preference may receive marketing until they opt out. A missing preference is not recorded
          as consent, and we honor existing opt-outs. We ask people who explicitly opt in to confirm
          they are 18 or older and are subscribing their own email address.
        </p>
        <p>
          We record your preference, the email address it applies to, the time and source of your
          choice, and the wording you accepted. We use our email delivery provider, Resend, to send
          messages and retain delivery records to operate and troubleshoot communications. Campaign
          audiences exclude deleted and disabled accounts and may be limited to accounts with
          activity in the last 90 days.
        </p>
        <p>
          You can stop marketing and administrator outreach using the unsubscribe link in campaign
          emails or Email preferences in account settings. You do not need to sign in to use an
          email unsubscribe link. We check opt-outs before handing new outreach messages to our
          delivery provider. A message already handed to the provider may still arrive. You can also
          contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
        <p>
          Opting out does not stop password resets, email verification, receipts, security notices,
          or other essential account messages. We do not include promotions in those messages. We
          retain a restricted suppression record derived from your email address to honor your
          opt-out, including after account deletion. Marketing resumes only if you explicitly
          subscribe again. Administrators cannot subscribe you on your behalf.
        </p>
      </section>
      <section>
        <h2>1. Who we are</h2>
        <p>
          Paddiq is a motorsports driver-development and race-operations platform for drivers,
          families, coaches, teams, and mechanics. Paddiq is launching with a focus on youth
          karting, with the broader goal of supporting drivers and racing programs across
          motorsports. In this policy, "Paddiq," "we," "us," and "our" refer to Paddiq LLC and the
          Paddiq services, including our website, web app, mobile app, support channels, and related
          services.
        </p>
      </section>

      <section>
        <h2>2. Information we collect</h2>
        <p>
          We collect information you provide, information created through your use of Paddiq, and
          limited information received from the services you choose to connect.
        </p>
        <ul>
          <li>
            <strong>Account information:</strong> name, email address, account identifier, role,
            team or family membership, profile photo, and account settings.
          </li>
          <li>
            <strong>Sign-in information:</strong> if you use Sign in with Apple or Google, we
            receive the provider account identifier and the name or email address the provider makes
            available. We do not receive your Apple or Google password.
          </li>
          <li>
            <strong>Team, family, and driver information:</strong> team names, roster details,
            driver profiles, parent or guardian links, staff assignments, schedules, coaching
            context, and related race-weekend records.
          </li>
          <li>
            <strong>Kart and garage information:</strong> kart details, assignments, setup history,
            maintenance records, notes, images, and other information entered by authorized users.
          </li>
          <li>
            <strong>Telemetry and session information:</strong> uploaded MyChron or compatible data
            files, lap and channel data, GPS coordinates recorded during a session, circuit
            information, timestamps, session metadata, comparisons, notes, and analysis generated in
            Paddiq.
          </li>
          <li>
            <strong>Billing information:</strong> plan, subscription state, invoices, payment
            status, billing contact information, and payment processor references. We do not store
            full credit card numbers on our servers.
          </li>
          <li>
            <strong>Support information:</strong> your name, email address, messages, attachments,
            troubleshooting details, and communications with us.
          </li>
          <li>
            <strong>Device and service information:</strong> browser or device type, operating
            system, IP address, app version, request timestamps, security events, error logs, and
            diagnostics needed to operate and protect the service.
          </li>
        </ul>
      </section>

      <section>
        <h2>3. Youth drivers and parent or guardian controls</h2>
        <p>
          Because Paddiq currently serves youth karting families and teams, some driver profiles and
          telemetry may relate to children. A parent, legal guardian, or team representative
          authorized by the parent or guardian should create or manage a child driver profile and
          decide who may access it. Team staff must only add or use a child's information when they
          have appropriate authority to do so.
        </p>
        <p>
          We use youth-driver information to provide driver profiles, session review, telemetry
          comparison, setup history, team collaboration, and driver-development features. We do not
          sell children's personal information or use it for targeted advertising.
        </p>
        <p>
          A parent or guardian may ask to review, correct, export, or delete a child's information,
          or withdraw permission for its continued use, by contacting{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We may need to verify the
          requester's identity, relationship to the child, and authority over the relevant team or
          family workspace. If you believe a child provided personal information without appropriate
          parent or guardian authorization, please contact us.
        </p>
      </section>

      <section>
        <h2>4. Mobile app permissions and local data</h2>
        <ul>
          <li>
            <strong>Local network:</strong> when you choose to connect to a MyChron device over
            local Wi-Fi, the iOS app accesses the local network to list or download sessions from
            that device. This connection is initiated by you.
          </li>
          <li>
            <strong>Files and photos:</strong> when you choose an import or profile-image feature,
            the app opens the system file or photo picker. Paddiq receives only the item you select,
            not your entire library.
          </li>
          <li>
            <strong>Data stored on your device:</strong> the app may temporarily keep downloaded
            sessions, upload state, saved MyChron connection details, authentication state, and
            preferences so features work reliably. Removing the app generally removes app-local
            data, but it does not delete information already uploaded to your Paddiq account.
          </li>
        </ul>
        <p>
          Paddiq does not request continuous background location access. Location coordinates shown
          in telemetry come from the racing data file or logger.
        </p>
      </section>

      <section>
        <h2>5. How we use information</h2>
        <ul>
          <li>Provide, maintain, secure, and improve Paddiq.</li>
          <li>
            Create and manage accounts, driver profiles, teams, roles, parent or guardian links,
            subscriptions, and support requests.
          </li>
          <li>
            Import and process telemetry, calculate lap and corner comparisons, and connect sessions
            with drivers, karts, circuits, and setups.
          </li>
          <li>
            Provide garage, maintenance, scheduling, collaboration, and driver-development
            workflows.
          </li>
          <li>
            Send account verification, team invitations, service messages, support replies, billing
            notices, and security alerts.
          </li>
          <li>Debug issues, prevent abuse, protect accounts, and comply with legal obligations.</li>
          <li>
            Review aggregated or de-identified service information to understand reliability and
            improve features.
          </li>
        </ul>
      </section>

      <section>
        <h2>6. How information is visible and shared</h2>
        <p>
          We share information only as needed to operate Paddiq, comply with law, protect rights, or
          with your direction.
        </p>
        <ul>
          <li>
            <strong>Within teams and family workspaces:</strong> driver, kart, session, setup,
            schedule, and related information may be visible to team owners, authorized staff,
            parents, guardians, and drivers according to their roles and permissions.
          </li>
          <li>
            <strong>At your direction:</strong> information may move between family and team
            workspaces when an authorized user links, transfers, or shares a driver or related
            record.
          </li>
          <li>
            <strong>Service providers:</strong> Google Firebase and Google Cloud provide
            authentication, database, storage, and hosting infrastructure; Apple and Google support
            optional sign-in; Stripe processes subscriptions and payments; and Resend supports
            service email. These providers process information for us under their terms and
            applicable contractual restrictions.
          </li>
          <li>
            <strong>Legal and safety:</strong> we may disclose information if required by law or if
            necessary to protect Paddiq, our users, or others.
          </li>
          <li>
            <strong>Business transfers:</strong> information may be transferred as part of a merger,
            acquisition, financing, or sale of assets, subject to this policy or a policy with
            similar protections.
          </li>
        </ul>
        <p>
          We do not sell personal information, rent user lists, or share personal information for
          third-party targeted advertising.
        </p>
      </section>

      <section>
        <h2>7. Payments</h2>
        <p>
          Payments and subscriptions are processed by Stripe. Stripe may collect payment card
          details and billing information directly under its own terms and privacy policy. Paddiq
          receives limited billing records such as customer identifiers, subscription status,
          invoice status, and plan information.
        </p>
      </section>

      <section>
        <h2>8. Cookies and similar technologies</h2>
        <p>
          We and our providers may use cookies, local storage, and similar technologies to keep you
          signed in, remember preferences, secure sessions, measure usage, and troubleshoot the
          service. Browser settings may let you limit cookies, but some features may not work
          correctly without them.
        </p>
      </section>

      <section>
        <h2>9. Data retention and deletion</h2>
        <p>
          We keep information for as long as needed to provide Paddiq, comply with legal
          obligations, resolve disputes, enforce agreements, and maintain security. Retention
          periods may vary by plan, data type, workspace settings, and legal requirements. Account
          holders can request account deletion through the app or contact us. Parents and guardians
          may also request deletion of a child's information as described above.
        </p>
        <p>
          Deleting an account may not immediately remove records controlled by a team or another
          authorized account, information another user is legally entitled to retain, or records we
          must keep for billing, fraud prevention, safety, dispute resolution, or legal compliance.
          Deleted information may remain in encrypted backups and system logs for a limited period
          before being overwritten.
        </p>
      </section>

      <section>
        <h2>10. Security</h2>
        <p>
          We use administrative, technical, and organizational safeguards designed to protect
          personal information. No system is perfectly secure, so we cannot guarantee that
          information will never be accessed, disclosed, altered, or destroyed without
          authorization.
        </p>
      </section>

      <section>
        <h2>11. Your choices and privacy rights</h2>
        <p>
          Depending on where you live, you may have rights to access, correct, delete, restrict,
          object to, or receive a copy of certain personal information and to withdraw consent where
          processing is based on consent. You can update some information and request account
          deletion inside the app. For other requests, contact us at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We may verify your identity and
          authority before completing a request. You may also have the right to appeal our response
          or complain to your local privacy regulator.
        </p>
      </section>

      <section>
        <h2>12. International data processing</h2>
        <p>
          Paddiq and our providers may process information in the United States and other countries.
          These countries may have privacy laws that differ from where you live. We take steps
          designed to protect information as described in this policy.
        </p>
      </section>

      <section>
        <h2>13. Third-party links and services</h2>
        <p>
          Paddiq may link to third-party websites, app stores, payment pages, or services. Their
          privacy practices are governed by their own policies. This policy covers Paddiq's handling
          of information, not a third party's independent practices.
        </p>
      </section>

      <section>
        <h2>14. Changes to this policy</h2>
        <p>
          We may update this policy from time to time. If we make material changes, we will update
          the effective date and may provide additional notice through the product, website, or
          email.
        </p>
      </section>

      <section>
        <h2>15. Contact us</h2>
        <p>
          Questions or privacy requests can be sent to{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </article>
  );
}
